/* eslint-disable no-nested-ternary */
import React, { useCallback, useMemo } from 'react';

import { runningInRNWebView } from '@eika-infrastruktur/mobilbank-lib';
import { AttentionMessage } from '@eika/attention-message';
import { Button, LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { WarningCard } from '@eika/card';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import { RichIconTypes } from '@eika/rich-icon';

import { EikaModal } from '../../components/EikaModal';
import { DismissKeyboardStickyFooter } from '../../components/shared/DismissKeyboardStickyFooter';
import { StickyFooterRow } from '../../components/shared/StickyFooter';
import { hasSigningError } from '../../components/transaction/Payment/PaymentForm';
import { useConfig } from '../../defaultConfig';
import type { ContactInfo } from '../../generated-types';
import useStepUp from '../../hooks/useStepUp';
import useTranslation from '../../hooks/useTranslation';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import { userTrackingIDs } from '../../utils/gtm/trackIDs/user';
import { StepUpLink } from './StepUpLink';
import UpdateInput, { UpdateWarning } from './UpdateInput';
import createUpdateContactInfoRequest from './createUpdateContactInfoRequest';
import useContactInfoFormContext from './form/useContactInfoFormContext';
import { cleanupEmail, getAddressJSX, getPhoneNumberJSX, removeWhitespaceOnSave } from './format';

type Props = {
    open?: boolean;
    setOpen: React.Dispatch<boolean>;
    onClose: () => void;
    isLoading?: boolean;
    contactInfo?: ContactInfo;
    isPeriodicControl?: boolean;
    modalId?: string;
};

export default ({
    open = false,
    setOpen,
    onClose,
    isLoading = false,
    contactInfo,
    isPeriodicControl = false,
    modalId = userTrackingIDs.contactInfo.id,
}: Props) => {
    const translation = useTranslation();
    const { featureToggles, messagesLink } = useConfig();

    const { handleChange, state, checkFormValidity } = useContactInfoFormContext();

    const trackId = isPeriodicControl ? userTrackingIDs.periodicControl.updateId : userTrackingIDs.contactInfo.updateId;

    const { signingStatus, startSigning, signingOperationPending, signingError, dispatch, showSigningFlow } =
        useSigningContext(
            createUpdateContactInfoRequest(
                contactInfo,
                featureToggles.enableUpdatingEmail,
                featureToggles.enableUpdatingMobilenumber,
            ),
        );

    const onSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (
                (await checkFormValidity()) &&
                (featureToggles.enableUpdatingEmail || featureToggles.enableUpdatingMobilenumber) &&
                !signingOperationPending
            ) {
                const cleanedEmailValue = removeWhitespaceOnSave(state.email ?? '');
                const cleanedMobileValue = removeWhitespaceOnSave(state.mobile ?? '');

                handleChange({ type: 'email', payload: cleanedEmailValue });
                handleChange({ type: 'mobile', payload: cleanedMobileValue });
                startSigning({ ...state, email: cleanedEmailValue, mobile: cleanedMobileValue });
            }
        },
        [state, startSigning, handleChange, checkFormValidity, featureToggles, signingOperationPending],
    );

    const retrySigning = () => {
        if (featureToggles.enableUpdatingEmail || featureToggles.enableUpdatingMobilenumber) {
            dispatch({ type: Actions.CLEAR_ERROR });
            const cleanedEmailValue = removeWhitespaceOnSave(state.email ?? '');
            const cleanedMobileValue = removeWhitespaceOnSave(state.mobile ?? '');
            startSigning({ ...state, email: cleanedEmailValue, mobile: cleanedMobileValue });
        }
    };

    const loading = isLoading || signingOperationPending;

    const isSigningError = signingError || hasSigningError(signingStatus);

    const { isHighAssurance, showStepupConfiguration, canStepup } = useStepUp([!isPeriodicControl]);

    const warning: UpdateWarning = useMemo(() => {
        if (!featureToggles.enableUpdatingMobilenumber && !isPeriodicControl) {
            return {
                type: 'attention',
                text: translation.contactInfo.warnings.mobile,
                link: messagesLink && (
                    <Link href={messagesLink.url} iconOnRightSide icon={IconTypes.PIL_HOEYRE}>
                        {translation.contactInfo.messages}
                    </Link>
                ),
            };
        }
        if (!canStepup) {
            return {
                type: 'attention',
                text: !runningInRNWebView
                    ? translation.contactInfo.warnings.mobileDesktop
                    : translation.contactInfo.warnings.mobile,
                link: messagesLink && (
                    <Link href={messagesLink.url} iconOnRightSide icon={IconTypes.PIL_HOEYRE}>
                        {translation.contactInfo.messages}
                    </Link>
                ),
            };
        }
        if (showStepupConfiguration) {
            return {
                type: 'warning',
                text: translation.contactInfo.stepUp.text,
                link: <StepUpLink />,
            };
        }
        return null;
    }, [
        featureToggles.enableUpdatingMobilenumber,
        isPeriodicControl,
        canStepup,
        showStepupConfiguration,
        translation.contactInfo.warnings.mobile,
        translation.contactInfo.warnings.mobileDesktop,
        translation.contactInfo.messages,
        translation.contactInfo.stepUp.text,
        messagesLink,
    ]);

    return (
        <EikaModal
            id={modalId}
            open={open}
            setOpen={setOpen}
            onClose={onClose}
            timeOut={signingStatus === 'COMPLETED' && !isSigningError ? 2000 : undefined}
            title={!showSigningFlow ? translation.contactInfo.editContactInfo : undefined}
            contentShouldStretch
        >
            {signingStatus !== 'COMPLETED' && !signingError && (
                <SigningContextFlow onClosePayment={onClose}>
                    <form onSubmit={onSubmit} noValidate>
                        <UpdateInput
                            disabled={signingOperationPending}
                            stateKey="email"
                            canUpdate={featureToggles.enableUpdatingEmail}
                            warning={{
                                type: 'attention',
                                text: translation.contactInfo.warnings.email,
                                link: messagesLink && (
                                    <Link href={messagesLink.url} iconOnRightSide icon={IconTypes.PIL_HOEYRE}>
                                        {translation.contactInfo.messages}
                                    </Link>
                                ),
                            }}
                            formatter={(value) => cleanupEmail({ value })}
                        />

                        <UpdateInput
                            disabled={signingOperationPending}
                            stateKey="mobile"
                            canUpdate={
                                (featureToggles.enableUpdatingMobilenumber || isPeriodicControl) && isHighAssurance
                            }
                            formatter={(value) => getPhoneNumberJSX({ value })}
                            warning={warning}
                        />

                        {!isPeriodicControl && contactInfo?.address && (
                            <section className="contact-information__wrapper">
                                <p className="contact-information__title bold">{translation.contactInfo.address}</p>
                                {contactInfo?.address ? (
                                    getAddressJSX()(contactInfo?.address, ', ')
                                ) : (
                                    <span className="contact-information__value-missing">
                                        {translation.contactInfo.addressMissing}
                                    </span>
                                )}
                                <AttentionMessage
                                    className="update-customer-info__warning"
                                    icon={IconTypes.INFO_20}
                                    type="info"
                                    text={translation.contactInfo.warnings.address}
                                />
                            </section>
                        )}
                        <StickyFooterRow className="contact-information__submit-wrapper">
                            <LinkButton
                                onClick={onClose}
                                data-track-id={trackId}
                                data-track-linkname={translation.cancel}
                            >
                                {translation.cancel}
                            </LinkButton>
                            {(featureToggles.enableUpdatingEmail || featureToggles.enableUpdatingMobilenumber) && (
                                <ButtonWithSpinner
                                    buttonType="positive"
                                    type="submit"
                                    disabled={signingOperationPending}
                                    showSpinner={loading}
                                    data-track-id={trackId}
                                    data-track-linkname={translation.contactInfo.save}
                                >
                                    {translation.contactInfo.save}
                                </ButtonWithSpinner>
                            )}
                        </StickyFooterRow>
                    </form>
                </SigningContextFlow>
            )}
            {signingStatus === 'COMPLETED' && (
                <section className="contact-information-success__wrapper">
                    <CheckmarkAnimation key={1} />
                    <span className="typography-subtitle">{translation.contactInfo.success}</span>
                    <Button className="success-button" data-testid="success-button" onClick={onClose}>
                        {translation.ok}
                    </Button>
                </section>
            )}
            {isSigningError && (
                <WarningCard
                    className="contact-information__error-wrapper"
                    title={translation.errors.ohNo}
                    isModal
                    icon={RichIconTypes.UNDRENDE}
                >
                    <div className="contact-information__sign-error-message">
                        <p className="typography-paragraph">{translation.contactInfo.signingError.message}</p>
                        <p className="typography-paragraph">{translation.contactInfo.signingError.contactMessage}</p>
                    </div>
                    <div className="error-button-wrapper">
                        <LinkButton onClick={() => dispatch({ type: Actions.RESET })}>{translation.cancel}</LinkButton>
                        <ButtonWithSpinner
                            showSpinner={signingOperationPending}
                            buttonType="positive"
                            onClick={retrySigning}
                        >
                            {translation.tryAgain}
                        </ButtonWithSpinner>
                    </div>
                </WarningCard>
            )}
            <DismissKeyboardStickyFooter />
        </EikaModal>
    );
};
